import { useState } from 'react';

export interface PickableUser {
  id: number;
  name: string;
  lastName: string;
  phoneNumber: string;
  companyName: string;
  role: string;
  userType: 'company' | 'agent' | 'other';
}

export const USER_TYPE_LABELS: Record<string, string> = { company: 'شرکتی', agent: 'نماینده', other: 'متفرقه' };

/**
 * Reusable searchable/multi-select user list with a همه/شرکتی/نماینده/متفرقه
 * (userType) filter — originally built for the SMS/notification page, also
 * used by the لیست قیمت page to assign customers to a price list.
 */
export function UserPicker({
  users,
  selectedIds,
  setSelectedIds,
}: {
  users: PickableUser[];
  selectedIds: Set<number>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<number>>>;
}) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'company' | 'agent' | 'other'>('all');

  const filtered = users.filter(u => {
    if (typeFilter !== 'all' && u.userType !== typeFilter) return false;
    const term = query.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(term) ||
      (u.lastName || '').toLowerCase().includes(term) ||
      (u.phoneNumber || '').includes(term) ||
      (u.companyName || '').toLowerCase().includes(term)
    );
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every(u => selectedIds.has(u.id));

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach(u => next.delete(u.id));
      else filtered.forEach(u => next.add(u.id));
      return next;
    });
  };

  const toggleUser = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '15px', color: '#333' }}>انتخاب کاربران</h3>
        <span style={{ fontSize: '13px', color: '#888' }}>{selectedIds.size} نفر انتخاب شده</span>
      </div>

      <div className="dashboardseaechBox" style={{ marginBottom: '10px' }}>
        <i className="dashMagnifierIcon"></i>
        <input
          className="dashSearchInput"
          placeholder="جستجو نام، شماره، شرکت"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {/* Filter by userType (نماینده/شرکتی) — "select all" only grabs the
          filtered group, so an admin can e.g. filter to شرکتی and select all. */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        {(['all', 'company', 'agent', 'other'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setTypeFilter(t)}
            style={{
              padding: '6px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
              border: `1.5px solid ${typeFilter === t ? '#14337D' : '#ddd'}`,
              background: typeFilter === t ? '#14337D' : '#fff',
              color: typeFilter === t ? '#fff' : '#555',
              fontFamily: 'inherit', fontWeight: 600,
            }}
          >
            {t === 'all' ? 'همه' : USER_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <table className="alternating-table listTable" style={{ width: '100%' }}>
        <thead>
          <tr className="dashTr1 firstTR">
            <th style={{ width: '40px' }}>
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
            </th>
            <th>نام</th>
            <th>موبایل</th>
            <th>شرکت</th>
            <th>نوع کاربر</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((user, index) => (
            <tr
              key={user.id}
              className={`dashTr2 ${index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}`}
              onClick={() => toggleUser(user.id)}
              style={{ cursor: 'pointer' }}
            >
              <td>
                <input
                  type="checkbox"
                  checked={selectedIds.has(user.id)}
                  onChange={() => toggleUser(user.id)}
                  onClick={e => e.stopPropagation()}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </td>
              <td>{[user.name, user.lastName].filter(Boolean).join(' ') || '—'}</td>
              <td style={{ direction: 'ltr', textAlign: 'right' }}>{user.phoneNumber}</td>
              <td>{user.companyName || '—'}</td>
              <td>{USER_TYPE_LABELS[user.userType] || '—'}</td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#aaa' }}>کاربری یافت نشد</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
